import { post } from 'lib/common/fetch'
import { API_URL } from 'lib/constants'
import { uuidv4 } from 'lib/helpers'
import { action, makeAutoObservable } from 'mobx'
import { Project, Notification, User, Organization } from 'types'
import { IRootStore } from './RootStore'

export interface IUiStore {
  language: 'en_US'
  theme: 'dark' | 'light'

  isPlatform?: boolean
  isDarkTheme: boolean
  selectedProject?: Project
  selectedOrganization?: Organization
  notification?: Notification
  profile?: User

  load: () => void
  toggleTheme: () => void
  setProjectRef: (ref?: string) => void
  setOrganizationSlug: (slug?: string) => void
  setNotification: (notification: Notification) => string
  setProfile: (value?: User) => void
}
export default class UiStore implements IUiStore {
  rootStore: IRootStore
  language: 'en_US' = 'en_US'
  theme: 'dark' | 'light' = 'dark'
  isPlatform = true
  // Once we're ready to hide platform only pages, uncomment below
  // isPlatform = (process?.env?.NEXT_PUBLIC_IS_PLATFORM ?? false) === 'true'

  selectedProjectRef?: string
  selectedOrganizationSlug?: string
  notification?: Notification
  profile?: User

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this, {
      setProjectRef: action,
      setOrganizationSlug: action,
    })
  }

  get selectedProject() {
    if (this.selectedProjectRef) {
      const found = this.rootStore.app.projects.find(
        (x: Project) => x.ref == this.selectedProjectRef
      )
      return found
    }
    return undefined
  }

  get selectedOrganization() {
    if (this.selectedOrganizationSlug) {
      const found = this.rootStore.app.organizations.find(
        (x: Organization) => x.slug == this.selectedOrganizationSlug
      )
      return found
    }
    if (this.selectedProjectRef) {
      const organizationId = this.selectedProject?.organization_id
      const found = this.rootStore.app.organizations.find(
        (x: Organization) => x.id == organizationId
      )
      return found
    }
    return undefined
  }

  get isDarkTheme() {
    return this.theme === 'dark'
  }

  load() {
    if (typeof window === 'undefined') return
    const savedTheme = (window.localStorage.getItem('theme') ?? 'dark') as 'dark' | 'light'
    this.setTheme(savedTheme)
  }

  toggleTheme() {
    if (this.theme === 'dark') {
      this.setTheme('light')
    } else {
      this.setTheme('dark')
    }
  }

  setTheme(theme: 'dark' | 'light') {
    this.theme = theme
    document.body.className = theme
  }

  setProjectRef(ref?: string) {
    this.selectedProjectRef = ref
  }

  setOrganizationSlug(slug?: string) {
    this.selectedOrganizationSlug = slug
  }

  setNotification(notification: Notification): string {
    const id = notification?.id ?? uuidv4()
    this.notification = { ...notification, id }
    return id
  }

  setProfile(value?: User) {
    if (value?.id != this.profile?.id) {
      this.profile = value

      if (value) {
        post(`${API_URL}/telemetry/identify`, { user: value })
      }
    }
  }
}
